/**
 * dashboardApi.js — Phase 83.4
 *
 * Small on-demand helper for endpoints that the Overview Dashboard
 * doesn't fetch as part of the initial page load. Right now this is just
 * the per-goal 12-month contribution timeline, but keeping it in a
 * shared module avoids duplicating the `authedGet` glue and makes it
 * obvious where new dashboard-scoped endpoints belong.
 *
 * The core `useDashboardData` hook still owns the initial parallel
 * fetch (snapshot, breakdown, reconciliation, briefing, ...). Drawers
 * and detail views call these helpers only when their surface is
 * actually opened.
 */
import axios from 'axios';
import { supabase } from '@/lib/supabaseClient';

const API = process.env.REACT_APP_BACKEND_URL;

async function authedGet(path) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const res = await axios.get(`${API}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return res.data;
}

/**
 * Fetch a goal's per-month contribution timeline.
 * @param {string} goalId
 * @param {number} months  1..36 (default 12)
 * @returns {Promise<{contributions:{ym,label,amount,txn_count}[], matching_rule:string, base_currency:string}>}
 */
export async function fetchGoalContributions(goalId, months = 12) {
  if (!goalId) return { contributions: [], matching_rule: 'none', base_currency: 'INR' };
  return authedGet(`/api/goals/${encodeURIComponent(goalId)}/contributions/timeline?months=${months}`);
}

/**
 * Force a fresh HYBRID briefing composition (usually right after an
 * import). The FE stores the result exactly as returned; the composer
 * upserts the weekly cache row server-side.
 */
export async function refreshDashboardBriefing() {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const res = await axios.post(`${API}/api/dashboard/briefing/refresh`, null, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return res.data;
}
